import React, { useState } from "react";
import { toast } from "sonner";
import { ClipboardPaste, MapPin, Plus, Trash2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

// Accepts lines like "9991 Zwischenwasser", "9991;Zwischenwasser", "Zwischenwasser, 9991" or tab-separated (Excel).
export const parsePostalList = (text) => {
  const ok = [];
  const invalid = [];
  text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).forEach((line) => {
    const m = line.match(/\b(\d{4,5})\b/);
    const city = m ? line.replace(m[0], "").replace(/^[\s;,\t|-]+|[\s;,\t|-]+$/g, "").trim() : "";
    if (m && city) ok.push({ plz: m[1], city });
    else invalid.push(line);
  });
  return { ok, invalid };
};

const mergeAreas = (current, incoming) => {
  const key = (a) => `${a.plz}|${a.city.toLowerCase()}`;
  const seen = new Set(current.map(key));
  const added = incoming.filter((a) => !seen.has(key(a)) && seen.add(key(a)));
  return { merged: [...current, ...added], added: added.length };
};

const BulkImport = ({ areas, setAreas, t }) => {
  const [text, setText] = useState("");
  const preview = text.trim() ? parsePostalList(text) : null;
  const apply = () => {
    const { merged, added } = mergeAreas(areas, preview.ok);
    setAreas(merged);
    setText("");
    toast.success(t("postal_import_done").replace("{added}", added).replace("{dupes}", preview.ok.length - added));
    if (preview.invalid.length) toast.warning(t("postal_import_invalid").replace("{n}", preview.invalid.length));
  };
  return (
    <div className="space-y-2 rounded-md border border-dashed border-border p-3" data-testid="postal-import">
      <div className="flex items-center gap-2 text-sm font-medium">
        <ClipboardPaste className="h-4 w-4 text-primary" /> {t("postal_import_title")}
      </div>
      <p className="text-xs text-muted-foreground">{t("postal_import_hint")}</p>
      <Textarea
        rows={4}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={"9991 Zwischenwasser\n9993;Oberzwischenwasser\nNiederbach, 9994"}
        className="font-mono-jb text-sm"
        data-testid="postal-import-textarea"
      />
      {preview && (
        <div className="text-xs" data-testid="postal-import-preview">
          <span className="text-primary">{t("postal_import_recognized").replace("{n}", preview.ok.length)}</span>
          {preview.invalid.length > 0 && (
            <span className="ml-2 text-destructive" title={preview.invalid.join("\n")}>
              {t("postal_import_invalid").replace("{n}", preview.invalid.length)}: {preview.invalid.slice(0, 3).join(" · ")}
            </span>
          )}
        </div>
      )}
      <Button type="button" variant="outline" size="sm" disabled={!preview || preview.ok.length === 0} onClick={apply} data-testid="postal-import-apply-btn">
        {t("postal_import_apply")}
      </Button>
    </div>
  );
};

export const RegistrationSettings = ({ muni, onSave, t }) => {
  const [areas, setAreas] = useState(muni.postal_areas || []);
  const [plz, setPlz] = useState("");
  const [city, setCity] = useState("");
  const valid = /^\d{4,5}$/.test(plz.trim()) && city.trim();

  const add = () => {
    if (!valid) return;
    setAreas([...areas, { plz: plz.trim(), city: city.trim() }]);
    setPlz("");
    setCity("");
  };

  return (
    <Card className="border-border" data-testid="registration-settings">
      <CardContent className="space-y-5 p-6">
        <div className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">{t("registration_settings")}</h3>
        </div>
        <div className="flex items-start justify-between gap-4 rounded-md border border-border p-4">
          <div>
            <div className="font-medium">{t("manual_user_approval")}</div>
            <p className="text-xs text-muted-foreground">{t("manual_user_approval_hint")}</p>
          </div>
          <Switch
            checked={!!muni.manual_user_approval}
            onCheckedChange={(v) => onSave({ manual_user_approval: v })}
            data-testid="manual-approval-switch"
          />
        </div>
        <div>
          <div className="font-medium">{t("postal_areas")}</div>
          <p className="mb-3 text-xs text-muted-foreground">{t("postal_areas_hint")}</p>
          <ul className="mb-3 space-y-2" data-testid="postal-areas-list">
            {areas.length === 0 && <li className="text-sm text-muted-foreground">{t("postal_areas_empty")}</li>}
            {areas.map((a, i) => (
              <li key={`${a.plz}-${a.city}-${i}`} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm" data-testid={`postal-area-${i}`}>
                <span className="font-mono-jb">{a.plz} · {a.city}</span>
                <Button size="sm" variant="ghost" onClick={() => setAreas(areas.filter((_, j) => j !== i))} data-testid={`postal-area-remove-${i}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
          <div className="grid grid-cols-[6rem_1fr_auto] gap-2">
            <Input placeholder={t("postal_code")} inputMode="numeric" maxLength={5} value={plz} onChange={(e) => setPlz(e.target.value)} data-testid="postal-area-plz-input" />
            <Input placeholder={t("city")} value={city} onChange={(e) => setCity(e.target.value)} data-testid="postal-area-city-input" />
            <Button variant="outline" onClick={add} disabled={!valid} data-testid="postal-area-add-btn"><Plus className="h-4 w-4" /></Button>
          </div>
          <div className="mt-3">
            <BulkImport areas={areas} setAreas={setAreas} t={t} />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button onClick={() => onSave({ postal_areas: areas })} data-testid="postal-areas-save-btn">{t("save")}</Button>
            {areas.length > 0 && (
              <Button variant="ghost" onClick={() => setAreas([])} data-testid="postal-areas-clear-btn">{t("postal_areas_clear")}</Button>
            )}
            <span className="text-xs text-muted-foreground">{t("postal_areas_unsaved_hint")}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
